/**
 * ═══════════════════════════════════════════════════════════════════════════
 * STATUS PALETTE — the single source of truth for every status color token.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Implements the "NextGen Status Color Standard" §01 core palette. Seven locked
 * hues, each with three steps. The step you pick is decided by WHAT you paint,
 * never by emphasis (a hard rule — the core step fails contrast as text):
 *
 *   tint  → cell fills, badge backgrounds        (never text)
 *   core  → non-text marks only: solid button fills, legend dots, chart marks
 *   deep  → all text: badge labels, numerals, glyphs on tint
 *
 * WHY a token module (per the standard's "Implementation Notes"): status colors
 * were previously scattered as raw Tailwind literals across ~30 components and
 * three unrelated config objects. Change one hue here and every surface that
 * reads from this module follows. Nothing hard-codes a hue anymore.
 *
 * WHY full class literals and not `bg-${hue}-100`: Tailwind v4 JIT only emits
 * classes it can see as complete strings at build time. Dynamic concatenation
 * would produce classes Tailwind never generates, so each token is a literal.
 *
 * Two families (indigo, fuchsia) sit deliberately OUTSIDE the seven cores: they
 * carry no outcome meaning and exist only for Scale B QA-alert categories that
 * already ship a badge (Status Changes, Dept Moved). See qaAlertConfig.ts §05.
 */

export type PaletteHue =
  | 'green'
  | 'yellow'
  | 'red'
  | 'orange'
  | 'purple'
  | 'grey'
  | 'blue'
  // Scale-B-only category palette (not outcome cores):
  | 'indigo'
  | 'fuchsia'

export interface HueTokens {
  /** tint — cell fills & badge backgrounds. Never used for text. */
  tintBg: string
  /** core — solid non-text marks: button fills, legend/chart dots. Never text. */
  coreBg: string
  coreText: string
  /** deep — all text on a tint background: labels, count numerals, glyphs. */
  deepText: string
  /** Hairline border matched to the tint step (badge outlines, cell edges). */
  border: string
  /**
   * Stronger border for OUTLINE action buttons (§04) — the border and icon carry
   * the hue, never a full fill. Deep step so it reads on a white button face.
   */
  borderStrong: string
}

/**
 * tint = -100, core = -500/-600, deep = -800. Grey maps to Tailwind `gray`;
 * "core" grey uses -200 as its fill because grey never needs a saturated mark.
 */
export const STATUS_PALETTE: Record<PaletteHue, HueTokens> = {
  green: {
    tintBg: 'bg-green-100',
    coreBg: 'bg-green-600',
    coreText: 'text-green-600',
    deepText: 'text-green-800',
    border: 'border-green-300',
    borderStrong: 'border-green-600',
  },
  yellow: {
    tintBg: 'bg-yellow-100',
    coreBg: 'bg-yellow-500',
    coreText: 'text-yellow-600',
    deepText: 'text-yellow-800',
    border: 'border-yellow-300',
    borderStrong: 'border-yellow-700',
  },
  red: {
    tintBg: 'bg-red-100',
    coreBg: 'bg-red-600',
    coreText: 'text-red-600',
    deepText: 'text-red-800',
    border: 'border-red-300',
    borderStrong: 'border-red-700',
  },
  orange: {
    tintBg: 'bg-orange-100',
    coreBg: 'bg-orange-600',
    coreText: 'text-orange-600',
    deepText: 'text-orange-800',
    border: 'border-orange-300',
    borderStrong: 'border-orange-700',
  },
  purple: {
    tintBg: 'bg-purple-100',
    coreBg: 'bg-purple-600',
    coreText: 'text-purple-600',
    deepText: 'text-purple-800',
    border: 'border-purple-300',
    borderStrong: 'border-purple-700',
  },
  grey: {
    tintBg: 'bg-gray-200',
    coreBg: 'bg-gray-200',
    coreText: 'text-gray-600',
    deepText: 'text-gray-700',
    border: 'border-gray-300',
    borderStrong: 'border-gray-700',
  },
  blue: {
    tintBg: 'bg-blue-100',
    coreBg: 'bg-blue-600',
    coreText: 'text-blue-600',
    deepText: 'text-blue-800',
    border: 'border-blue-300',
    borderStrong: 'border-blue-600',
  },
  indigo: {
    tintBg: 'bg-indigo-100',
    coreBg: 'bg-indigo-500',
    coreText: 'text-indigo-500',
    deepText: 'text-indigo-800',
    border: 'border-indigo-300',
    borderStrong: 'border-indigo-600',
  },
  fuchsia: {
    tintBg: 'bg-fuchsia-100',
    coreBg: 'bg-fuchsia-500',
    coreText: 'text-fuchsia-500',
    deepText: 'text-fuchsia-800',
    border: 'border-fuchsia-300',
    borderStrong: 'border-fuchsia-600',
  },
}

/**
 * Convenience: the standard badge treatment (tint fill + deep text + tint-matched
 * border) as one space-joined class string. Consumers that render a QA-alert or
 * status badge should use this so the tint/deep/border steps always travel together.
 */
export const badgeClassFor = (hue: PaletteHue): string => {
  const tokens = STATUS_PALETTE[hue]
  return `${tokens.tintBg} ${tokens.deepText} ${tokens.border}`
}

/**
 * Standard §04 action-button treatment: white face, hue carried by a colored
 * border + icon/text only — never a full fill. `primary` uses a heavier border
 * (border-2) for the single desired action (Resolve the Miss). The icon inherits
 * the color via `currentColor`, so the caller renders the icon without its own
 * color class.
 */
export const outlineButtonClassFor = (hue: PaletteHue, primary = false): string => {
  const tokens = STATUS_PALETTE[hue]
  const borderWeight = primary ? 'border-2' : 'border'
  return `bg-white hover:bg-gray-50 ${borderWeight} ${tokens.borderStrong} ${tokens.deepText}`
}
